<template>
  <div>
    <b-col>
      <b-row>
        <b-form-group class="w-100" id="country-group" :label="$t('xCountry')" label-for="country">
          <b-form-select
            id="country"
            v-model="$v.formSelections.country.$model"
            :options="[{ text: $t('xSelect'), value: null, disabled: true }, ...countryOptions]"
            :state="validState($v.formSelections.country)"
            @change="setStateOptions()"
            aria-describedby="country-invalid"
          />
          <b-form-invalid-feedback id="country-invalid"> {{ $t('xErrorCountryEmpty') }} </b-form-invalid-feedback>
        </b-form-group>
      </b-row>

      <b-row>
        <b-form-group class="w-100" id="line1-group" :label="$t('xAddress')" label-for="line1">
          <b-form-input
            id="line1"
            v-model="$v.formSelections.address1.$model"
            :state="validState($v.formSelections.address1)"
            maxlength="50"
            type="text"
            aria-describedby="line1-invalid"
          ></b-form-input>
          <b-form-invalid-feedback id="line1-invalid">
            {{ getErrorMessageForField('address1') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-row>
      <b-row>
        <b-form-group class="w-100" id="line2-group" :label="$t('xAddressLine2Optional')" label-for="line2">
          <b-form-input
            id="line2"
            v-model="$v.formSelections.address2.$model"
            :state="validState($v.formSelections.address2)"
            type="text"
            maxLength="50"
            aria-describedby="line2-invalid"
          ></b-form-input>
          <b-form-invalid-feedback id="line2-invalid">{{ $t('xErrorAddressInvalid') }}</b-form-invalid-feedback>
        </b-form-group>
      </b-row>

      <b-row>
        <b-form-group class="w-100" id="city-group" :label="$t('xCity')" label-for="city">
          <b-form-input
            id="city"
            v-model="$v.formSelections.city.$model"
            maxLength="50"
            :state="validState($v.formSelections.city)"
            aria-labelledby="city-invalid"
          />
          <b-form-invalid-feedback id="city-invalid"> {{ getErrorMessageForField('city') }} </b-form-invalid-feedback>
        </b-form-group>
      </b-row>

      <b-row>
        <b-form-group class="w-100" id="state-group" :label="$t('xStateRegion')" label-for="state">
          <b-form-select
            id="state"
            v-model="$v.formSelections.state.$model"
            :state="validState($v.formSelections.state)"
            :options="[{ text: $t('xSelect'), value: null, disabled: true }, ...stateOptions]"
            aria-describedby="state-invalid"
          ></b-form-select>
          <b-form-invalid-feedback id="state-invalid"> {{ $t('xErrorRegionEmpty') }} </b-form-invalid-feedback>
        </b-form-group>
      </b-row>

      <b-row>
        <b-form-group class="w-100" id="postalCode-group" :label="$t('xPostalCode')" label-for="postalCode">
          <b-form-input
            id="postalCode"
            v-model="$v.formSelections.postalCode.$model"
            type="text"
            maxlength="10"
            :state="validState($v.formSelections.postalCode)"
            debounce="500"
            aria-describedby="postalCode-invalid"
          ></b-form-input>
          <b-form-invalid-feedback id="postalCode-invalid">
            {{ getErrorMessageForField('postalCode') }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-row>
    </b-col>
  </div>
</template>
<script>
import { validationMixin } from 'vuelidate'
import { minLength, required, requiredIf } from 'vuelidate/lib/validators'
import { mapState } from 'vuex/dist/vuex.common.js'

const ADDRESS_PATTERN = /^[a-zA-Z0-9\s.,'/-]+$/

const matchAddressPattern = value => ADDRESS_PATTERN.test(value)

// City validator - only letters, spaces, and common punctuation (no numbers)
const isCityValid = value => {
  if (!value) return true
  return /^[a-zA-Z\s.,'/-]+$/.test(value)
}

// Postal code validators
const isAlphanumeric = value => {
  if (!value) return true
  return /^[a-zA-Z0-9\s-]+$/.test(value)
}

const validateUSMexicoPostalCode = (value, vm) => {
  if (!value) return true
  const country = vm.country
  if (country !== 'US' && country !== 'MX') return true

  // Must match pattern: 5 digits optionally followed by hyphen and 4 digits
  const pattern = /^\d{5}(-\d{4})?$/
  return pattern.test(value)
}

const validateCanadaPostalCode = (value, vm) => {
  if (!value) return true
  const country = vm.country
  if (country !== 'CA') return true

  const withoutSpaces = value.replace(/\s/g, '')
  if (withoutSpaces.length < 6) return false

  // Must be alphanumeric
  return /^[a-zA-Z0-9]+$/.test(withoutSpaces)
}

const validBillingCountries = ['US', 'CA']

const unitedStatesTerritories = ['PR', 'VI', 'GU', 'AS', 'MP', 'UM']

const errorTextMap = {
  address1: {
    required: 'xErrorAddressLine1Empty',
    matchAddressPattern: 'xErrorAddressInvalid'
  },
  city: {
    required: 'xErrorCityEmpty',
    isCityValid: 'xErrorCityInvalid'
  },
  postalCode: {
    required: 'xErrorPostalCodeEmpty',
    isAlphanumeric: 'xPostalCodeInvalid',
    validateUSMexicoPostalCode: 'xPostalCodeInvalid',
    validateCanadaPostalCode: 'xPostalCodeInvalid'
  }
}

export default {
  mixins: [validationMixin],
  props: ['isBillingAddress'],
  emits: ['setIsFormInvalid', 'setSubmittedAddress'],
  data() {
    return {
      countryOptions: [],
      stateOptions: [],
      formSelections: {
        country: null,
        address1: '',
        address2: '',
        city: '',
        state: null,
        postalCode: ''
      }
    }
  },
  validations() {
    return {
      formSelections: {
        country: {
          required
        },
        address1: {
          required,
          minLength: minLength(1),
          matchAddressPattern
        },
        address2: {
          minLength: minLength(1),
          matchAddressPatternIfPresent: val => !val || ADDRESS_PATTERN.test(val)
        },
        city: {
          required,
          minLength: minLength(1),
          isCityValid
        },
        state: {
          required: requiredIf(() => {
            if (this.$v.formSelections?.country && this.stateOptions.length < 1) {
              // if the country has been selected but there are no states (eg Puerto Rico), user can't select anything
              return false
            }

            return true
          })
        },
        postalCode: {
          required,
          isAlphanumeric,
          validateUSMexicoPostalCode,
          validateCanadaPostalCode
        }
      }
    }
  },
  methods: {
    onShow() {
      // Added to address ML-2187
      // Only reset form if there isn't an existing, complete address already input
      if (this.formIsEmpty) {
        this.resetForm()
      }
    },
    populateWithAddress(address) {
      // Populate form with provided address data
      if (address) {
        this.formSelections = {
          country: address.country || null,
          address1: address.line1 || '',
          address2: address.line2 || '',
          city: address.city || '',
          state: address.region || null,
          postalCode: address.postalCode || ''
        }
        // Load state options for the selected country
        if (this.formSelections.country) {
          this.setStateOptions()
        }
      }
    },
    setStateOptions() {
      if (this.formSelections.country) {
        this.stateOptions = this.countryStates.value[this.formSelections.country].countryRegions?.map(reg => ({
          value: reg.abbreviation,
          text: reg.name.replace(/\s*\(.*?\)/g, '') // Removes everything inside of parentheses
        }))
      } else {
        this.stateOptions = []
        this.formSelections.state = null
        this.$v.formSelections.state.$reset() // clear form data for state
      }
    },
    resetForm() {
      this.setStateOptions()
      this.formSelections = {
        country: null,
        address1: '',
        address2: '',
        city: '',
        state: null,
        postalCode: ''
      }
      this.$v.formSelections.state.$reset() // clear form data for state
      this.$v.$reset()
    },
    getErrorMessageForField(field) {
      if (this.$v.formSelections?.[field]?.$error) {
        if (!this.$v.formSelections?.[field]?.required) {
          return this.$t(errorTextMap[field].required)
        }

        // Check all validators defined for this field in errorTextMap
        const validators = Object.keys(errorTextMap[field]).filter(key => key !== 'required')
        for (const validator of validators) {
          if (this.$v.formSelections[field]?.[validator] === false) {
            return this.$t(errorTextMap[field][validator])
          }
        }
      }
    }
  },
  computed: {
    ...mapState({
      countryStates: state => state.application.countryStates
    }),
    formIsEmpty() {
      // If all required parts of an address aren't present,
      // the user couldn't have made it to the verify address screen and form can be considered empty
      return !(
        this.formSelections.country &&
        this.formSelections.address1 &&
        this.formSelections.city &&
        this.formSelections.postalCode &&
        this.formSelections.state
      )
    }
  },
  watch: {
    formSelections: {
      handler(newVal) {
        this.$emit('setIsFormInvalid', this.$v.$invalid || this.$v.$anyError)
        this.$emit('setSubmittedAddress', newVal)
      },
      deep: true
    }
  },
  async mounted() {
    if (!this.countryStates || !this.countryStates.value) {
      await this.$store.dispatch('getCountrySubdivisions')
    }
    const validCountries = this.isBillingAddress
      ? this.countryStates?.countryCodes?.filter(code => validBillingCountries.includes(code)) // only US and CA are viable billing countries
      : this.countryStates?.countryCodes?.filter(code => !unitedStatesTerritories.includes(code)) // otherwise, all but the US territories (will be added to US in either scenario)
    this.countryOptions = validCountries.map(code => ({
      value: code,
      text: this.countryStates.value[code].countryName
    }))
  }
}
</script>
